import { gql } from '@apollo/client';
import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TruncatedCell } from '~components/organization/AccessRequests/AccessRequestsSection';
import { HandleApiError } from '~graphql/HandleApiError';
import {
  ServiceAdminSearchCustomersQuery,
  useServiceAdminSearchCustomersQuery,
} from './__generated__/CustomerSearchResults.graphql';
import { AccessStateCell } from './AccessStateCell';
import { RequestAccessModal } from './RequestAccessModal';
import { SearchResultColumn, SearchResultsTable, useSearchPagination } from './SearchResultsTable';

type CustomerResult =
  ServiceAdminSearchCustomersQuery['serviceAdminSearchCustomers']['results'][number];

export function CustomerSearchResults({ searchTerm }: { searchTerm: string }) {
  const { t } = useTranslation();
  const pagination = useSearchPagination();
  const [requestingAccessTo, setRequestingAccessTo] = useState<{ id: string; name: string } | null>(
    null,
  );

  const { data, loading, error, refetch } = useServiceAdminSearchCustomersQuery({
    variables: { searchTerm, pageSize: pagination.pageSize, pageNumber: pagination.pageIndex },
    fetchPolicy: 'cache-and-network',
  });

  const columns = useMemo<Array<SearchResultColumn<CustomerResult>>>(
    () => [
      {
        id: 'name',
        header: t('name'),
        render: (customer) => (
          <TruncatedCell text={customer.name} maxWidth="48" fontWeight="semibold" />
        ),
      },
      {
        id: 'handle',
        header: t('handle'),
        render: (customer) => <TruncatedCell text={customer.handle} maxWidth="32" />,
      },
      {
        id: 'organization',
        header: t('organisation'),
        render: (customer) => <TruncatedCell text={customer.organizationName} maxWidth="48" />,
      },
      {
        id: 'displayCount',
        header: t('displayCount'),
        render: (customer) => customer.displayCount,
      },
      {
        id: 'onboardedAdmin',
        header: t('onboardedAdmin'),
        render: (customer) => (
          <TruncatedCell text={customer.onboardedAdminEmail ?? '-'} maxWidth="40" />
        ),
      },
      {
        id: 'createdAt',
        header: t('dateCreated'),
        render: (customer) => format(new Date(customer.createdAt), 'MMM d, yyyy'),
      },
      {
        id: 'accessState',
        header: '',
        render: (customer) => (
          <AccessStateCell
            organizationId={customer.organizationId}
            accessState={customer.accessState}
            onRequestAccess={() =>
              setRequestingAccessTo({
                id: customer.organizationId,
                name: customer.organizationName,
              })
            }
          />
        ),
      },
    ],
    [t],
  );

  if (error) {
    return <HandleApiError error={error} />;
  }

  return (
    <>
      <SearchResultsTable
        isLoading={loading && !data}
        rows={data?.serviceAdminSearchCustomers.results ?? []}
        totalCount={data?.serviceAdminSearchCustomers.totalCount ?? 0}
        columns={columns}
        pagination={pagination}
      />
      <RequestAccessModal
        organization={requestingAccessTo}
        onClose={() => setRequestingAccessTo(null)}
        onRequested={refetch}
      />
    </>
  );
}

CustomerSearchResults.graphql = {
  queries: {
    ServiceAdminSearchCustomers: gql`
      query ServiceAdminSearchCustomers($searchTerm: String!, $pageSize: Int, $pageNumber: Int) {
        serviceAdminSearchCustomers(
          searchTerm: $searchTerm
          pageSize: $pageSize
          pageNumber: $pageNumber
        ) {
          totalCount
          results {
            id
            name
            handle
            organizationId
            organizationName
            displayCount
            onboardedAdminEmail
            createdAt
            accessState
          }
        }
      }
    `,
  },
};
